// src/modules/IntegratedMessaging/adapters/IntegratedMessagingApiAdapter.ts

import { HttpClient } from '@adapters/HttpClient';
import { IntegratedMessaging as IntegratedMessagingModel } from '../models/IntegratedMessaging';

export class IntegratedMessagingApiAdapter {
  constructor(private httpClient: HttpClient) {}

  /**
   * Devuelve todas las integraciones de courier.
   */
  async getCourierIntegrations(): Promise<IntegratedMessagingModel[]> {
    const response = await this.httpClient.get<{
      data: IntegratedMessagingModel[];
    }>('/couriers/integrations');
    return response.data;
  }

  /**
   * Activa una integración existente.
   */
  async activateIntegration(id: number): Promise<void> {
    await this.httpClient.put(`/couriers/integrations/${id}/activate`, {});
  }

  /**
   * Desactiva una integración existente.
   */
  async deactivateIntegration(id: number): Promise<void> {
    await this.httpClient.put(`/couriers/integrations/${id}/desactivate`, {});
  }

  /**
   * Actualiza una integración existente.
   * @param id ID de la integración.
   * @param payload Opciones de actualización.
   */
  async updateIntegration(
    id: number,
    payload: {
      name?: string | null;
      data: Record<string, any>;
      visibility?: 'public' | 'private';
      private_team_ids?: number[];
    }
  ): Promise<void> {
    await this.httpClient.put(`/couriers/integrations/${id}`, {
      name: payload.name,
      data: payload.data,
      ...(payload.visibility && { visibility: payload.visibility }),
      ...(payload.private_team_ids && { private_team_ids: payload.private_team_ids }),
    });
  }

  /**
   * Actualiza la visibilidad de una integración existente.
   * @param id ID de la integración.
   * @param visibility Visibilidad ('public' o 'private').
   * @param private_team_ids IDs de los teams privados.
   */
  async updateIntegrationVisibility(
    id: number,
    visibility: 'public' | 'private',
    private_team_ids?: number[]
  ): Promise<void> {
    await this.httpClient.put(`/couriers/integrations/${id}/visibility`, {
      visibility,
      private_team_ids: private_team_ids || [],
    });
  }

  /**
   * Crea una nueva integración de courier.
   */
  async createIntegration(payload: {
    courier_id: number;
    name: string;
    data: Record<string, any>;
    visibility?: 'public' | 'private';
    private_team_ids?: number[];
  }): Promise<void> {
    await this.httpClient.post(`/couriers/integrations`, {
      courier_id: payload.courier_id,
      name: payload.name,
      data: payload.data,
      ...(payload.visibility && { visibility: payload.visibility }),
      ...(payload.private_team_ids && { private_team_ids: payload.private_team_ids }),
    });
  }
}
